import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Container, Divider, Link, Stack,
  Typography,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useAuth } from '../contexts/AuthContext';
import { useEnvironment } from '../contexts/EnvironmentContext';
import bookingService from '../services/bookingService';

/**
 * The diary: who is coming in, and when.
 *
 * It used to be the bottom half of Settings → Booking, under the opening hours and the service
 * list, the most operational screen in the product, filed where configuration goes and reached
 * in three clicks. Somebody checks this between customers; it belongs beside Submissions.
 *
 * Setting booking UP stays in Settings, where it is done once.
 */
const DAYS = 14;

const isoDate = (date) => date.toISOString().slice(0, 10);

const fortnight = () => {
  const from = new Date();
  const to = new Date(from.getTime() + DAYS * 24 * 60 * 60 * 1000);
  return { from: isoDate(from), to: isoDate(to) };
};

/** "Thu 12 Jun, 09:30" in the reader's own locale. */
const formatWhen = (isoInstant) => {
  const when = new Date(isoInstant);
  return Number.isNaN(when.getTime())
    ? isoInstant
    : when.toLocaleString(undefined, {
        weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
      });
};

/** The day heading a booking sits under, so a fortnight reads as days rather than a list. */
const dayOf = (isoInstant) => {
  const when = new Date(isoInstant);
  return Number.isNaN(when.getTime())
    ? 'Later'
    : when.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
};

const BookingsPage = () => {
  const { authenticatedFetch } = useAuth();
  const { selectedEnvironment } = useEnvironment();
  const envId = selectedEnvironment?.id;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [live, setLive] = useState(null);

  const range = useMemo(fortnight, []);

  const load = useCallback(async () => {
    if (!envId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const [status, upcoming] = await Promise.all([
        bookingService.keyStatus(envId, authenticatedFetch).catch(() => null),
        bookingService.listBookings(envId, range, authenticatedFetch),
      ]);
      setLive(!!status?.valid);
      setBookings(Array.isArray(upcoming) ? upcoming : (upcoming?.items ?? []));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch, envId, range]);

  useEffect(() => { load(); }, [load]);

  const handleCancel = async (bookingId) => {
    try {
      await bookingService.cancelBooking(envId, bookingId, authenticatedFetch);
      setBookings((current) => current.filter((booking) => booking.id !== bookingId));
    } catch (e) {
      setError(e.message);
    }
  };

  // Grouped by day, in the order the API returned them (soonest first).
  const days = useMemo(() => {
    const grouped = new Map();
    bookings.forEach((booking) => {
      const day = dayOf(booking.startsAt);
      grouped.set(day, [...(grouped.get(day) || []), booking]);
    });
    return [...grouped.entries()];
  }, [bookings]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>Bookings</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Everyone booked in over the next {DAYS} days. Customers pick from your real availability,
        so nothing here is double-booked.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
      ) : !envId ? (
        <Alert severity="info">Select an environment to see its bookings.</Alert>
      ) : bookings.length === 0 ? (
        <Card>
          <CardContent>
            <Stack spacing={1.5} alignItems="flex-start">
              <EventAvailableIcon color="disabled" />
              <Typography variant="h6">No bookings yet</Typography>
              <Typography variant="body2" color="text.secondary">
                {live
                  ? 'When somebody books through one of your flows, they appear here.'
                  : 'Live booking is not switched on for this environment yet, so a flow cannot offer real times.'}
              </Typography>
              <Button
                component={RouterLink}
                to="/settings?section=booking"
                variant={live ? 'text' : 'contained'}
              >
                {live ? 'Booking setup' : 'Set up booking'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {days.map(([day, forThatDay]) => (
            <Card key={day}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>{day}</Typography>
                {forThatDay.map((booking, index) => (
                  <Box key={booking.id}>
                    {index > 0 && <Divider />}
                    {/* Customer name and contact, excluded from session recordings. */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      className="ph-no-capture"
                      sx={{ py: 1.25 }}
                    >
                      <Box>
                        <Typography variant="body2">
                          {formatWhen(booking.startsAt)}, {booking.customerName || 'No name given'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {[booking.serviceName, booking.customerWaId].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                      <Button size="small" onClick={() => handleCancel(booking.id)}>Cancel</Button>
                    </Stack>
                  </Box>
                ))}
              </CardContent>
            </Card>
          ))}
          <Typography variant="caption" color="text.secondary">
            Hours, services and calendar sync live in{' '}
            <Link component={RouterLink} to="/settings?section=booking">Booking setup</Link>.
          </Typography>
        </Stack>
      )}
    </Container>
  );
};

export default BookingsPage;
